import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("mn-theme") || "dark");
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("mn-cart")) || [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [coupon, setCoupon] = useState(null);

  useEffect(() => {
    localStorage.setItem("mn-theme", theme);
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      root.classList.remove("light");
    } else {
      root.classList.remove("dark");
      root.classList.add("light");
    }
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("mn-cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, variantLabel, qty = 1) => {
    const variant = product.variants.find((v) => v.label === variantLabel) || product.variants[0];
    setCart((prev) => {
      const key = `${product.slug}__${variant.label}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: Math.min(10, i.qty + qty) } : i));
      }
      return [
        ...prev,
        {
          key,
          slug: product.slug,
          name: product.name,
          image: product.images[0],
          collection: product.collection,
          variant: variant.label,
          price: variant.price,
          qty,
        },
      ];
    });
    setDrawerOpen(true);
  };

  const updateQty = (key, qty) => {
    if (qty < 1) {
      setCart((prev) => prev.filter((i) => i.key !== key));
      return;
    }
    setCart((prev) => prev.map((i) => (i.key === key ? { ...i, qty: Math.min(10, qty) } : i)));
  };

  const removeItem = (key) => setCart((prev) => prev.filter((i) => i.key !== key));
  const clearCart = () => {
    setCart([]);
    setCoupon(null);
  };

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const count = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

  const value = {
    theme,
    setTheme,
    toggleTheme: () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    cart,
    addToCart,
    updateQty,
    removeItem,
    clearCart,
    drawerOpen,
    setDrawerOpen,
    coupon,
    setCoupon,
    subtotal,
    count,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
